import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, 
  FaBriefcase,
  FaCalendarAlt,
  FaBookmark, 
  FaRegBookmark,
  FaArrowRight
} from 'react-icons/fa';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import Spinner from '../Spinner';

const JobCard = ({ job, showSaveButton = true }) => {
  const { currentUser } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [orgDetails, setOrgDetails] = useState({ 
    name: 'Posted by Parent', 
    logo: null,
    isParent: true
  });
  
  // Fetch organization details
  useEffect(() => {
    const fetchOrgDetails = async () => {
      if (!job.posterId) {
        setOrgDetails({ name: 'Posted by Parent', logo: null, isParent: true });
        return;
      }
      
      try {
        const orgDoc = await getDoc(doc(db, 'organizations', job.posterId));
        if (orgDoc.exists()) {
          const data = orgDoc.data();
          setOrgDetails({
            name: data.orgName || data.name || 'Organization',
            logo: data.logoUrl || data.photoURL || null,
            isParent: false
          });
        }
      } catch (error) {
        console.error('Error fetching organization details:', error);
        setOrgDetails({ name: 'Posted by Parent', logo: null, isParent: true });
      }
    };
    
    fetchOrgDetails();
  }, [job.posterId]);

  // Format date helper
  const formatDate = (date) => {
    if (!date) return 'Recently';
    const d = date.toDate ? date.toDate() : new Date(date);
    const now = new Date();
    const diffInDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format location
  const formatLocation = (location) => {
    if (!location) return 'Location not specified';
    if (typeof location === 'string') return location;
    return [location.area, location.city, location.state, location.country]
      .filter(Boolean)
      .join(', ');
  };

  // Format skills
  const formatSkills = (skills) => {
    if (!Array.isArray(skills)) return [];
    return skills.slice(0, 3); // Show only first 3 skills
  };

  // Check if job is saved when component mounts
  useEffect(() => {
    const checkIfSaved = async () => {
      if (!currentUser) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const savedJobs = userDoc.data().savedJobs || [];
          setIsSaved(savedJobs.includes(job.id));
        }
      } catch (error) {
        console.error('Error checking saved status:', error);
      }
    };
    
    checkIfSaved();
  }, [currentUser, job.id]);

  // Handle save/unsave job
  const handleSaveJob = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!currentUser) {
      toast.info('Please sign in to save jobs');
      return;
    }
    
    setIsSaving(true);
    
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      
      if (isSaved) {
        await updateDoc(userRef, {
          savedJobs: arrayRemove(job.id)
        });
        setIsSaved(false);
        toast.success('Job removed from saved jobs');
      } else {
        await updateDoc(userRef, {
          savedJobs: arrayUnion(job.id)
        });
        setIsSaved(true);
        toast.success('Job saved successfully');
      }
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error('Failed to save job. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Job type related code removed as per request

  const handleCardClick = () => {
    window.location.href = `/jobs/${job.id}`;
  };

  return (
    <div 
      onClick={handleCardClick}
      className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200 border border-gray-100 hover:border-blue-100 cursor-pointer"
    >
        <div className="p-6">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div className="w-full">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-1 hover:text-blue-600 transition-colors">
                        {job.title || 'Job Title'}
                      </h3>
                      <p className={`${orgDetails.isParent ? 'text-sm text-gray-500' : 'text-gray-600'} mb-0.5`}>
                        {orgDetails.name}
                      </p>
                    </div>
                    {orgDetails.logo && (
                      <img 
                        src={orgDetails.logo} 
                        alt="Organization logo"
                        className="w-10 h-10 rounded-lg object-cover border-2 border-white shadow-sm"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                </div>
                
                {/* Job type badge removed as per request */}
              </div>
              
              {/* Job Meta - Location and Posted Date in one row */}
              <div className="mt-1.5 space-y-1">
                <div className="flex flex-wrap gap-4 items-center text-sm text-gray-600">
                  <div className="flex items-center">
                    <FaMapMarkerAlt className="mr-2 text-blue-500 flex-shrink-0" />
                    <span>{formatLocation(job.location)}</span>
                  </div>
                  
                  {job.postedAt && (
                    <div className="flex items-center text-gray-500">
                      <FaCalendarAlt className="mr-2 text-gray-400 flex-shrink-0" />
                      <span>Posted {formatDate(job.postedAt)}</span>
                    </div>
                  )}
                </div>
                
                {/* Skills */}
                {job.skills && job.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formatSkills(job.skills).map((skill, index) => (
                      <span 
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Description Preview */}
              {job.description && (
                <div className="mt-3 text-sm text-gray-600 line-clamp-2">
                  {job.description.substring(0, 150)}{job.description.length > 150 ? '...' : ''}
                </div>
              )}
              
              {/* Save Button */}
              <div className="mt-4 flex justify-end">
                {showSaveButton && currentUser && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSaveJob(e);
                    }}
                    disabled={isSaving}
                    className="inline-flex items-center text-sm text-gray-500 hover:text-blue-600 transition-colors px-2 py-1 -mr-2 -mb-2"
                    title={isSaved ? 'Remove from saved jobs' : 'Save job'}
                  >
                    {isSaving ? (
                      <Spinner size="small" />
                    ) : isSaved ? (
                      <FaBookmark className="text-blue-600" />
                    ) : (
                      <FaRegBookmark />
                    )}
                    <span className="ml-1">{isSaved ? 'Saved' : 'Save'}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default JobCard;
